import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';

import fileDownload from 'react-file-download';
import { useRouteMatch } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
} from '@mui/material';

import ErrorDisplay from '~/components/Common/ErrorDisplay';
import Spinner from '~/components/Common/Spinner';

import { getRatePlansPDF } from '~/services/PDFService';
import ReservationService from '~/services/ReservationService';

import { reportError } from '~/utils/reportError';

import RatePlanList from '../../Common/RatePlanList';

import { CopyRatePlansModal } from './CopyRatePlansModal';

type Props = {
  vendorName?: string;
  offers: Array<any>;
  properties: Array<App.Property>;
};

type RoomLink = {
  id: string;
  name: string;
  link: string;
  propertyId: string;
};

type RoomOption = {
  id: string;
  name: string;
};

export default function VendorRatePlansPageContainer({ offers, properties, vendorName }: Props) {
  const {
    params: { id_vendor: vendorId },
  } = useRouteMatch<{ id_vendor: string }>();

  const [ratePlans, setRatePlans] = useState([]);
  const [ratePlansActive, setRatePlansActive] = useState([]);
  const [ratePlansUnassigned, setRatePlansUnassigned] = useState([]);
  const [roomsByRatePlan, setRoomsByRatePlan] = useState<Record<string, Array<RoomLink>>>({});
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [isCloning, setCloning] = useState(false);
  const [isCopyModalVisible, setCopyModalVisible] = useState(false);
  const [key, setKey] = useState<'all' | 'active' | 'unassigned'>('all');
  const [selectedPropertyId, setSelectedPropertyId] = useState('all');
  const [selectedRoomId, setSelectedRoomId] = useState('all');

  const propertyRoomOptions = useMemo(() => {
    if (selectedPropertyId === 'all') {
      return [];
    }

    const selectedProperty = (properties || []).find((property) => property.id === selectedPropertyId);

    if (!selectedProperty) {
      return [];
    }

    return selectedProperty.room_types
      .map<RoomOption>((roomType) => ({ id: roomType.id, name: roomType.name }))
      .sort((leftRoom, rightRoom) => leftRoom.name.localeCompare(rightRoom.name));
  }, [properties, selectedPropertyId]);

  const roomsForDisplayByRatePlan = useMemo(() => {
    return Object.entries(roomsByRatePlan).reduce<Record<string, Array<RoomLink>>>((acc, [ratePlanId, rooms]) => {
      const filteredRooms =
        selectedPropertyId === 'all' ? rooms : rooms.filter((room) => room.propertyId === selectedPropertyId);

      if (filteredRooms.length > 0) {
        acc[ratePlanId] = filteredRooms;
      }

      return acc;
    }, {});
  }, [roomsByRatePlan, selectedPropertyId]);

  const hasRoomMatch = useCallback(
    (ratePlanId: string) => {
      const rooms = roomsForDisplayByRatePlan[ratePlanId] ?? [];

      if (!rooms.length) {
        return false;
      }

      if (selectedRoomId === 'all') {
        return true;
      }

      return rooms.some((room) => room.id === selectedRoomId);
    },
    [roomsForDisplayByRatePlan, selectedRoomId],
  );

  const editBtnFormatter = (ratePlanId: string) => {
    return (
      <Button component={Link} to={`/vendors/${vendorId}/rate-plans/${ratePlanId}`} variant="text">
        Edit
      </Button>
    );
  };

  const cloneBtnFormatter = (ratePlanId: string) => {
    return (
      <Button variant="text" color="error" onClick={() => cloneRatePlan(ratePlanId)} disabled={isCloning}>
        Clone {isCloning && <Spinner size={15} inline />}
      </Button>
    );
  };

  const filteredRatePlans = useCallback(
    (inputRatePlans: Array<any>) => {
      if (selectedPropertyId === 'all') {
        return inputRatePlans;
      }

      return inputRatePlans.filter((ratePlan) => {
        return hasRoomMatch(ratePlan.id);
      });
    },
    [hasRoomMatch, selectedPropertyId],
  );

  const tabsMap = useMemo(
    () => ({
      all: {
        title: 'All Rate Plans',
        component: RatePlanList,
        props: {
          ratePlans: filteredRatePlans(ratePlans),
          editBtnFormatter,
          cloneBtnFormatter,
          roomsByRatePlan: roomsForDisplayByRatePlan,
        },
      },
      active: {
        title: 'Active Rate Plans',
        component: RatePlanList,
        props: {
          ratePlans: filteredRatePlans(ratePlansActive),
          editBtnFormatter,
          cloneBtnFormatter,
          roomsByRatePlan: roomsForDisplayByRatePlan,
        },
      },
      unassigned: {
        title: 'Unassigned Rate Plans',
        component: RatePlanList,
        props: {
          ratePlans: filteredRatePlans(ratePlansUnassigned),
          editBtnFormatter,
          cloneBtnFormatter,
          roomsByRatePlan: roomsForDisplayByRatePlan,
        },
      },
    }),
    [
      cloneBtnFormatter,
      editBtnFormatter,
      filteredRatePlans,
      ratePlans,
      ratePlansActive,
      ratePlansUnassigned,
      roomsForDisplayByRatePlan,
    ],
  );

  type TabKey = keyof typeof tabsMap;
  const allTabs = Object.keys(tabsMap);
  const currentTab = tabsMap[key];

  const handleChange = useCallback((_: SyntheticEvent, newKey: TabKey) => {
    setKey(newKey);
  }, []);

  const handlePropertyChange = useCallback((event: SelectChangeEvent) => {
    setSelectedPropertyId(event.target.value);
    setSelectedRoomId('all');
  }, []);

  const handleRoomChange = useCallback((event: SelectChangeEvent) => {
    setSelectedRoomId(event.target.value);
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    ReservationService.getRatePlans(vendorId)
      .then((data) => {
        const allRatePlans = data.result;
        setRatePlans(allRatePlans);

        const roomRates = new Map();
        const mappedRatePlans = new Map();
        const roomsByRatePlanMap = new Map<string, Map<string, RoomLink>>();

        (properties || []).forEach((property) => {
          property.room_types.forEach((roomType) => {
            roomType.room_rates.forEach((roomRate) => {
              if (!mappedRatePlans.get(roomRate.rate_plan.id)) {
                mappedRatePlans.set(roomRate.rate_plan.id, {
                  id: roomRate.rate_plan.id,
                  propertyDisabled: property.deleted_at !== null,
                  hasOffer: false,
                  offerActive: false,
                  rates: [],
                });
              }

              const ratePlanRooms = roomsByRatePlanMap.get(roomRate.rate_plan.id) ?? new Map<string, RoomLink>();
              ratePlanRooms.set(roomType.id, {
                id: roomType.id,
                name: roomType.name,
                propertyId: property.id,
                link: `/vendors/${vendorId}/properties/${property.id}/room-types/${roomType.id}/room-rates/${roomRate.id}`,
              });
              roomsByRatePlanMap.set(roomRate.rate_plan.id, ratePlanRooms);

              const plan = mappedRatePlans.get(roomRate.rate_plan.id);
              plan.rates.push(roomRate.id);
              if (property.deleted_at === null) {
                plan.propertyDisabled = false;
              }
              roomRates.set(roomRate.id, roomRate.rate_plan.id);
            });
          });
        });

        const mappedRoomsByRatePlan = Array.from(roomsByRatePlanMap.entries()).reduce<Record<string, Array<RoomLink>>>(
          (acc, [ratePlanId, roomTypes]) => {
            acc[ratePlanId] = Array.from(roomTypes.values()).sort((leftRoom, rightRoom) =>
              leftRoom.name.localeCompare(rightRoom.name),
            );

            return acc;
          },
          {},
        );

        setRoomsByRatePlan(mappedRoomsByRatePlan);

        (offers || []).forEach((offer) => {
          offer?.packages?.forEach((pkg) => {
            pkg?.package_options?.forEach((option) => {
              const ratePlanId = roomRates.get(option.fk_room_rate_id);
              if (ratePlanId) {
                const ratePlan = mappedRatePlans.get(ratePlanId);
                if (ratePlan) {
                  ratePlan.hasOffer = true;
                  ratePlan.offerActive = offer.status === 'content-approved';
                }
              }
            });
          });
        });

        const list = Array.from(mappedRatePlans.values());
        const active = list.filter((record) => record.offerActive).map((record) => record.id);
        const unassigned = list.filter((record) => !record.hasOffer).map((record) => record.id);

        setRatePlansActive(allRatePlans.filter((ratePlan) => active.includes(ratePlan.id)));
        setRatePlansUnassigned(allRatePlans.filter((ratePlan) => unassigned.includes(ratePlan.id)));
      })
      .catch((err) => {
        console.warn(err);
        setError("Service error. Can't load vendor rate plans");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [vendorId, offers, properties]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <Spinner />;
  }

  if (error) {
    return <ErrorDisplay message={error} />;
  }

  const cloneRatePlan = async (id: string) => {
    setCloning(true);
    try {
      await ReservationService.cloneRatePlan(id);
      fetchData();
    } catch (error) {
      reportError(error);
    } finally {
      setCloning(false);
    }
  };

  const createRatePlansDetails = () => {
    return ratePlans.map((eachPlan) => {
      const comm_or_markup = eachPlan.rate_type === 'net' ? eachPlan.markup : eachPlan.commission;
      return {
        name: eachPlan.name,
        type: eachPlan.rate_type,
        comm_or_markup,
      };
    });
  };

  const downloadRatePlans = async () => {
    setDownloading(true);
    setDownloadFailed(false);

    try {
      const list = createRatePlansDetails();
      const pdf = await getRatePlansPDF({
        list,
        propertyName: vendorName,
      });
      const fileName = `LuxuryEscapes-Rate-Plans-${vendorId}.pdf`;

      await fileDownload(pdf, fileName);
    } catch (error) {
      console.warn(error);
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  const onCopyRatePlansModalClose = (isSaved = false) => {
    setCopyModalVisible(false);

    if (isSaved) {
      fetchData();
    }
  };

  return (
    <div>
      <Box textAlign="right" mb={2}>
        <Button
          onClick={() => setCopyModalVisible(true)}
          variant="text"
          startIcon={<ContentCopyIcon />}
          aria-label="Copy Rate Plans"
        >
          Copy
        </Button>

        {ratePlans && (
          <Button type="button" variant="text" onClick={downloadRatePlans}>
            {isDownloading && <CircularProgress size={16} style={{ marginRight: '5px' }} />}
            {!isDownloading && <DownloadIcon />}
            {isDownloading && <span>Downloading Rate Plans</span>}
            {!isDownloading && downloadFailed && <span>Download Failed! Try Again</span>}
          </Button>
        )}
      </Box>

      <Stack mb={2} direction="row" spacing={2}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="rate-plan-property-filter-label">Properties</InputLabel>
          <Select
            labelId="rate-plan-property-filter-label"
            id="rate-plan-property-filter"
            value={selectedPropertyId}
            label="Properties"
            onChange={handlePropertyChange}
          >
            <MenuItem value="all">All Properties</MenuItem>
            {(properties || []).map((property) => (
              <MenuItem key={property.id} value={property.id}>
                {property.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedPropertyId !== 'all' && (
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="rate-plan-room-filter-label">Rooms</InputLabel>
            <Select
              labelId="rate-plan-room-filter-label"
              id="rate-plan-room-filter"
              value={selectedRoomId}
              label="Rooms"
              onChange={handleRoomChange}
            >
              <MenuItem value="all">All Rooms</MenuItem>
              {propertyRoomOptions.map((roomOption) => (
                <MenuItem key={roomOption.id} value={roomOption.id}>
                  {roomOption.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={key} onChange={handleChange} id="rate-plans">
          {allTabs.map((tabKey) => (
            <Tab key={tabKey} label={tabsMap[tabKey].title} value={tabKey} />
          ))}
        </Tabs>
      </Box>

      <Box pt={4}>
        <Box component={currentTab.component} {...currentTab.props} />
      </Box>

      <Button variant="contained" component={Link} to={`/vendors/${vendorId}/rate-plans`}>
        + New
      </Button>

      <CopyRatePlansModal isOpen={isCopyModalVisible} onClose={onCopyRatePlansModalClose} />
    </div>
  );
}
